import { useQuery } from "@tanstack/react-query";
import api from "../api";

export interface Member {
  member_type: any;
  member_type_name: any;
  role_id: any;
  role_name: any;
  pikitup_member_id: number;
  member_first_name: string;
  member_surname: string;
  official_id: string;
}

interface MembersResponse {
  statusCode: number;
  message: string;
  data: {
    members: Member[];
  };
}

// --- Reusable serializer ---
const serializeParams = (params: Record<string, any>) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      searchParams.append(key, `[${value.join(",")}]`);
    } else if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
};

// --- Fetch members ---
const fetchMembers = async (
  memberType?: number | number[],
  filters: Record<string, any> = {}
): Promise<Member[]> => {
  try {
    const params: Record<string, any> = {
      client_type: "ADMIN",
      ...filters, // ✅ spread all dynamic filters coming from UI
    };

    // only add member_types_in if not admin (1)
    if (memberType !== 1) {
      params.member_types_in = Array.isArray(memberType)
        ? memberType
        : [memberType];
    }

    const { data } = await api.get<MembersResponse>("/members/v1", {
      params,
      paramsSerializer: serializeParams,
    });

    return data?.data?.members ?? [];
  } catch (err: any) {
    if (err.response?.status === 404) {
      return [];
    }
    console.error("Error fetching members:", err);
    return [];
  }
};

// --- Hook ---
export const useMembers = (
  memberType: number | number[],
  filters: Record<string, any> = {}
) =>
  useQuery({
    queryKey: ["members", memberType, filters], // ✅ reacts to filter changes
    queryFn: () => fetchMembers(memberType, filters),
    enabled: !!memberType,
    retry: 1,
  });
